/**
 * Track list sidebar for the vinyl player.
 * Shows numbered tracks with play/pause icons, animated soundbars for the
 * active track, and a draggable progress bar at the bottom of the current track.
 */
import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { cn } from "@/lib/utils";
import type { VinylTrack } from "@/hooks/useVinylPlayer";

interface TrackSelectorProps {
  tracks: VinylTrack[];
  currentTrackIndex: number;
  trackProgress: number; // 0-1 progress within current track
  onSelectTrack: (index: number) => void;
  onTogglePlayPause?: () => void;
  onSeekInTrack?: (progress: number) => void; // 0-1 seek within current track
  isPlaying: boolean;
}

// Format duration from seconds to mm:ss
const formatDuration = (seconds?: number): string => {
  if (!seconds) return "--:--";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const TrackSelector = ({ tracks, currentTrackIndex, trackProgress, onSelectTrack, onTogglePlayPause, onSeekInTrack, isPlaying }: TrackSelectorProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Handle progress bar click/drag
  const handleProgressBarInteraction = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !onSeekInTrack) return;
    e.stopPropagation();
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, clickX / rect.width));
    onSeekInTrack(progress);
  }, [onSeekInTrack]);

  const handleProgressDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsDragging(true);
    handleProgressBarInteraction(e);
  }, [handleProgressBarInteraction]);

  const handleProgressDrag = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    handleProgressBarInteraction(e);
  }, [isDragging, handleProgressBarInteraction]);

  const handleProgressDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <motion.div
      className="flex flex-col w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="flex flex-col">
        {tracks.map((track, index) => {
          const isCurrentTrack = currentTrackIndex === index;
          const isHovered = hoveredIndex === index;
          const isCurrentlyPlaying = isCurrentTrack && isPlaying;

          return (
            <motion.button
              key={track.title}
              className={cn(
                "relative flex items-center gap-3 py-3 px-2 border-b border-border",
                "text-left transition-all duration-200",
                "hover:bg-card hover:pl-4",
                "focus:outline-none focus-visible:bg-card focus-visible:pl-4",
                isCurrentTrack && "bg-card pl-4"
              )}
              onClick={() => {
                if (isCurrentTrack && onTogglePlayPause) {
                  // Clicking on current track toggles play/pause
                  onTogglePlayPause();
                } else {
                  // Clicking on different track seeks to it
                  onSelectTrack(index);
                }
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.3 }}
              aria-current={isCurrentTrack ? 'true' : undefined}
            >
              {/* Track number / playing indicator */}
              <div className="relative w-6 h-6 flex items-center justify-center">
                {isCurrentlyPlaying && !isHovered ? (
                  // Animated soundbars when playing (not hovered)
                  <motion.div
                    className="flex gap-0.5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    {[0, 1, 2].map((bar) => (
                      <motion.div
                        key={bar}
                        className="w-0.5 bg-foreground"
                        animate={{
                          height: [8, 16, 8],
                        }}
                        transition={{
                          duration: 0.5,
                          repeat: Infinity,
                          delay: bar * 0.15,
                          ease: "easeInOut",
                        }}
                      />
                    ))}
                  </motion.div>
                ) : isCurrentlyPlaying && isHovered ? (
                  // Pause icon when hovering currently playing track
                  <Pause size={14} className="text-foreground" />
                ) : isCurrentTrack && !isPlaying && isHovered ? (
                  // Play icon when hovering paused current track
                  <Play size={14} className="text-foreground ml-0.5" />
                ) : isHovered ? (
                  // Play icon when hovering non-current track
                  <Play size={14} className="text-foreground ml-0.5" />
                ) : (
                  // Track number (default)
                  <span className="font-body text-xs text-muted-foreground">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                )}
              </div>

              {/* Track info */}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "font-display text-sm tracking-tight whitespace-nowrap overflow-hidden text-ellipsis",
                  isCurrentTrack ? "text-foreground" : "text-foreground/80"
                )}>
                  {track.title}
                </p>
                <p className="font-body text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {track.isOriginal ? "Thursday Jam" : `Cover • ${track.artist}`}
                </p>
              </div>

              {/* Track duration */}
              <div className="font-body text-xs text-muted-foreground tabular-nums">
                {formatDuration(track.duration)}
              </div>

              {/* Progress bar for current track - draggable */}
              {isCurrentTrack && (
                <div 
                  ref={progressBarRef}
                  className="absolute bottom-0 left-0 right-0 h-2 bg-muted cursor-pointer group"
                  onClick={handleProgressBarInteraction}
                  onMouseDown={handleProgressDragStart}
                  onMouseMove={handleProgressDrag}
                  onMouseUp={handleProgressDragEnd}
                  onMouseLeave={handleProgressDragEnd}
                >
                  {/* Visual track (thinner) */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-muted-foreground/20" />
                  {/* Progress fill */}
                  <div 
                    className="absolute bottom-0 left-0 h-0.5 bg-foreground/60 group-hover:h-1 transition-all"
                    style={{ width: `${trackProgress * 100}%` }}
                  />
                  {/* Drag handle */}
                  <div 
                    className="absolute bottom-0 h-2 w-2 bg-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity -translate-x-1/2"
                    style={{ left: `${trackProgress * 100}%` }}
                  />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default TrackSelector;
